<script setup lang="ts">
import { provide, ref } from 'vue'

import PillGroup from '../pill/PillGroup.vue'
import PillInput from '../pill/PillInput.vue'
import TabSheetTab from './TabSheetTab.vue'
import TabSheetTabGroup from './TabSheetTabGroup.vue'
import { TabSheetType } from './helpers'
import {
  type TTabsheetType,
  type TabsheetTabDefinition,
  tabsheetKey,
} from './types'

const props = withDefaults(
  defineProps<{
    /**
     * Unique identifier for this tabsheet. Used internally to create ARIA
     * identifiers.
     */
    id: string

    /**
     * Controls the presentation of the tab interface.
     */
    type?: TTabsheetType
  }>(),
  { type: TabSheetType.Tabs }
)

const tabs = ref<TabsheetTabDefinition[]>([])
const activeIndex = ref(0)

const containerComponent =
  props.type === TabSheetType.Pills ? PillGroup : TabSheetTabGroup
const tabComponent = props.type === TabSheetType.Pills ? PillInput : TabSheetTab

/**
 * Called by `<TabSheetPane>` instances to register themselves with the main tab
 * controller.
 * @param tab The tab definition
 */
const registerTab = (tab: TabsheetTabDefinition) => {
  if (tab.selected === true) {
    activeIndex.value = tabs.value.length
  }
  tabs.value.push(tab)
  return tabs.value.length - 1
}

/**
 * Called by `<TabSheetPane>` instances whenever their props change and need to
 * notify the main controller
 * @param id The tab ID that changed
 * @param patch The changes to apply
 */
const updateTab = (id: number, patch: Partial<TabsheetTabDefinition>) => {
  const tab = tabs.value[id]
  if (tab) {
    Object.assign(tab, patch)
  }
}

/**
 * Called by `<TabSheetPane>` instances to unregister itself when unmounting
 * @param index The tab index to remove
 */
const unregisterTab = (index: number) => {
  tabs.value.splice(index, 1)
}

/**
 * The tabsheet controller context that will be injected into each
 * `<TabSheetPane>`
 */
provide(tabsheetKey, {
  registerTab,
  unregisterTab,
  updateTab,
  activeIndex,
  tabs,
  id: props.id,
})
</script>

<template>
  <div class="glp-tabsheet">
    <component
      :is="containerComponent"
      v-model="activeIndex"
      role="tablist"
      class="glp-tabsheet__tabs"
    >
      <component
        :is="tabComponent"
        v-for="(tab, index) in tabs"
        :id="`${id}-tab-${index}`"
        :key="index"
        :icon="tab.icon"
        :label="tab.title"
        :value="index"
        role="tab"
        :aria-controls="`${id}-panel-${index}`"
      />
    </component>
    <div class="glp-tabsheet__panes">
      <slot></slot>
    </div>
  </div>
</template>

<style>
.glp-tabsheet {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
}
.glp-tabsheet__panes {
  flex: 1;
  overflow: auto;
}
</style>
